import { Center, Spacer, Spinner, toast, Toaster } from 'components/ui-kit';
import { Form, Formik, FormikHelpers } from 'formik';
import { isErrorWithMessage, Profile } from 'lib';
import {
  isTochkaEmail,
  RUSSIAN_PHONE_REGEXP,
  EMAIL_REGEXP,
} from 'lib/validators/validators';
import { DeliveryTypeCode, OrderCreateResponse } from 'modules/cart/api';
import { useCartContext } from 'modules/cart/providers';
import { orderService } from 'modules/cart/services';
import { useRouter } from 'next/navigation';
import { useMemo, useRef } from 'react';
import * as yup from 'yup';
import {
  CommentForm,
  DeliveryForm,
  DeliveryTypeForm,
  PesonalDataForm,
  SubmitForm,
} from './components';
import { RUSSIA_CODE } from './offer-form.consts';
import { TOfferForm } from './offer-form.types';

const validationSchemaRussia = yup.object().shape({
  country: yup.string().required(),
  city: yup.string().required(),
  address1: yup.string().required(),
  postcode: yup.string().required(),
  name: yup.string().required(),
  email: yup
    .string()
    .email()
    .matches(EMAIL_REGEXP, 'Проверьте правильность ввода email')
    .required(),
  lastName: yup.string().required(),
  phone: yup.string().matches(RUSSIAN_PHONE_REGEXP).required(),
  shippingTypeCode: yup.string().required(),
  comment: yup.string(),
  state: yup.string(),
  agreement: yup.boolean().oneOf([true], 'Нужно ваше согласие'),
});

const validationSchemaOtherCountry = yup.object().shape({
  country: yup.string().required(),
  city: yup.string().required(),
  address1: yup.string().required(),
  postcode: yup.string(),
  name: yup.string().required(),
  email: yup
    .string()
    .email()
    .matches(EMAIL_REGEXP, 'Проверьте правильность ввода email')
    .required(),
  lastName: yup.string().required(),
  phone: yup.string().required(),
  comment: yup.string(),
  agreement: yup.boolean().oneOf([true], 'Поле является обязательным'),
});

type TOfferFormProps = {
  profile?: Profile | null;
};

const INITIAL_VALUES_FORM: TOfferForm = {
  city: '',
  address1: '',
  postcode: '',
  name: '',
  email: '',
  lastName: '',
  phone: '',
  comment: '',
  shippingTypeCode: DeliveryTypeCode.COURIER_RUSSIA,
  state: '',
  country: RUSSIA_CODE,
  agreement: true,
};

const getProfileData = (
  profile: Profile,
  initValues: TOfferForm,
): TOfferForm => {
  return {
    ...initValues,
    name: profile.firstName ?? initValues.name,
    lastName: profile.lastName ?? initValues.lastName,
    phone: profile.phone ?? initValues.phone,
    email: profile.email,
    address1: profile.addresses[0]?.address1 ?? initValues.address1,
    city: profile.addresses[0]?.city ?? initValues.city,
    state: profile.addresses[0]?.state ?? initValues.state,
    postcode: profile.addresses[0]?.postcode ?? initValues.postcode,
    agreement: true,
  };
};

export const OfferForm = ({ profile }: TOfferFormProps): JSX.Element | null => {
  const { cart, cartIsLoading } = useCartContext();
  const formRef = useRef<HTMLFormElement | null>(null);
  const router = useRouter();

  const initValues = useMemo(
    () =>
      profile
        ? getProfileData(profile, INITIAL_VALUES_FORM)
        : INITIAL_VALUES_FORM,
    [profile],
  );

  if (cartIsLoading)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  if (!cart) return null;

  const handleSubmit = (
    values: TOfferForm,
    { setSubmitting }: FormikHelpers<TOfferForm>,
  ): void => {
    void orderService
      .create(values)
      .then((response: OrderCreateResponse | undefined | void) => {
        if (response) {
          void router.push(response.confirmation.confirmationUrl);
        }
        setSubmitting(false);
      })
      .catch((error: unknown) => {
        if (isErrorWithMessage(error)) {
          toast.error(error.message);
        }
        setSubmitting(false);
      });
  };

  return (
    <Formik<TOfferForm>
      initialValues={initValues}
      onSubmit={handleSubmit}
      validationSchema={() =>
        yup.lazy((values: TOfferForm) => {
          const IS_FOREIGN_COUNTRY =
            RUSSIA_CODE !== values.country?.toLowerCase();

          return IS_FOREIGN_COUNTRY
            ? validationSchemaOtherCountry
            : validationSchemaRussia;
        })
      }
    >
      {() => {
        return (
          <Form placeholder="" ref={formRef} autoComplete="off">
            <>
              <DeliveryForm />
              <Spacer size={48} />
              <DeliveryTypeForm
                price={cart.priceForDelivery}
                isStaff={isTochkaEmail(profile?.email)}
              />
              <Spacer size={48} />
              <PesonalDataForm />
              <Spacer size={48} />
              <CommentForm />
              <Spacer size={48} />
              <SubmitForm formRef={formRef} />
              <Toaster />
            </>
          </Form>
        );
      }}
    </Formik>
  );
};
